/** Same charset as WorkflowGeneralInfoView.validateWorkflowName. */
const WORKFLOW_NAME_REGEX = /^[A-Za-z0-9._\-]+$/;
/** Same folder charset as WorkflowGeneralInfoView.onSLPchange. */
const SOURCE_PATH_REGEX = /^\/([a-zA-Z0-9#_\-]+\/)*[a-zA-Z0-9#_\-]+\/$/;
const SOURCE_PATH_SEGMENT_EDGE = /^[a-zA-Z0-9]+$/;

export function isWorkflowNameFormatValid(name: unknown): boolean {
  return typeof name === "string" && name.length > 0 && name.length <= 64 && WORKFLOW_NAME_REGEX.test(name);
}

/** Mirrors GI onSLPchange validity (start/end `/`, no spaces, folder charset, alnum edges). */
export function isSourceLocationPathFormatValid(path: unknown): boolean {
  if (typeof path !== "string" || !path) return false;
  if (!path.startsWith("/") || !path.endsWith("/") || /\s/.test(path)) return false;
  if (!SOURCE_PATH_REGEX.test(path)) return false;
  return path.split("/").every(
    (seg) => !seg || (SOURCE_PATH_SEGMENT_EDGE.test(seg[0]) && SOURCE_PATH_SEGMENT_EDGE.test(seg.slice(-1))),
  );
}

function isPullTransfer(transferType: unknown): boolean {
  return String(transferType ?? "").trim().toLowerCase() === "pull";
}

export function hasNonEmptyTrimmed(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * ACME-1960: an edit whose path still matches what loaded. The stored value can predate
 * current GI format rules, so re-blocking it would strand the workflow — the field is
 * disabled and the user has no way to correct it.
 */
export function isUnchangedPersistedEditPath(
  workflowData: Record<string, unknown> | null | undefined,
  path: unknown,
): boolean {
  if (!workflowData?.isEdit) return false;
  if (!hasNonEmptyTrimmed(path)) return false;
  const loaded = workflowData.loadedSourceLocationSuffix;
  if (typeof loaded !== "string" || !loaded) return false;
  return String(path).toLowerCase() === loaded.toLowerCase();
}

function trimStringField(workflowData: Record<string, unknown>, key: string): void {
  const value = workflowData[key];
  if (typeof value === "string") workflowData[key] = value.trim();
}

/** Tab checkmark / clone Save enable — same presence rules as save, without mutating. */
export function isGeneralInfoTabComplete(workflowData: Record<string, unknown> | null | undefined): boolean {
  if (!workflowData) return false;
  return (
    hasNonEmptyTrimmed(workflowData.WorkflowName) &&
    hasNonEmptyTrimmed(workflowData.TransferType) &&
    hasNonEmptyTrimmed(workflowData.BusinessUnitParticipant) &&
    !!workflowData.isSourceLocationValid &&
    !!workflowData.isWorkflowNameValid
  );
}

/**
 * ACME-1952: GI unmounts on tab switch — derive flags from stored fields, then valid.
 * PULL source path is server-stamped (GI treats it as valid). PUSH uses GI path rules,
 * plus the ACME-1960 escape hatch for an unchanged persisted path on edit.
 * Edit PULL keeps a non-empty stored name (the field is disabled).
 */
export function syncGeneralInfoValid(workflowData: Record<string, unknown> | null | undefined): void {
  if (!workflowData) return;

  trimStringField(workflowData, "TransferType");
  trimStringField(workflowData, "BusinessUnitParticipant");

  const pull = isPullTransfer(workflowData.TransferType);
  workflowData.isWorkflowNameValid =
    workflowData.isEdit && pull
      ? hasNonEmptyTrimmed(workflowData.WorkflowName)
      : isWorkflowNameFormatValid(workflowData.WorkflowName);
  workflowData.isSourceLocationValid =
    pull ||
    isSourceLocationPathFormatValid(workflowData.sourceLocationPath) ||
    isUnchangedPersistedEditPath(workflowData, workflowData.sourceLocationPath);
  workflowData.valid = !!(
    hasNonEmptyTrimmed(workflowData.WorkflowName) &&
    hasNonEmptyTrimmed(workflowData.TransferType) &&
    hasNonEmptyTrimmed(workflowData.BusinessUnitParticipant) &&
    workflowData.isWorkflowNameValid &&
    workflowData.isSourceLocationValid
  );
}
